import { useState } from "react";
import { useDispatch } from "react-redux";
import { updateSystemConfig } from "../../store/actions/adminActions";
import Swal from "sweetalert2";

export function useSystemConfigActions(systemConfig, refreshCallback) {
    const dispatch = useDispatch();
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [configFormData, setConfigFormData] = useState(null);
    const [configError, setConfigError] = useState(null);
    const [isSavingConfig, setIsSavingConfig] = useState(false);

    // Initialize form data when system config changes
    const initializeFormData = () => {
        if (systemConfig) {
            setConfigFormData({
                plotPrice: systemConfig.plotPrice,
                nonMemberPlotPrice: systemConfig.nonMemberPlotPrice,
                minimumPercentage: systemConfig.minimumPercentage,
                maximumInstallments: systemConfig.maximumInstallments,
                availablePlots: {
                    blossom: { ...systemConfig.availablePlots.blossom },
                    university: { ...systemConfig.availablePlots.university }
                }
            });
        }
    };

    const handleConfigInputChange = (e) => {
        const { name, value } = e.target;

        if (name.includes('.')) {
            const [parent, child, property] = name.split('.');
            setConfigFormData({
                ...configFormData,
                [parent]: {
                    ...configFormData[parent],
                    [child]: {
                        ...configFormData[parent][child],
                        [property]: Number(value)
                    }
                }
            });
        } else {
            setConfigFormData({
                ...configFormData,
                [name]: Number(value)
            });
        }
    };

    const handleStatusChange = (estate, value) => {
        setConfigFormData({
            ...configFormData,
            availablePlots: {
                ...configFormData.availablePlots,
                [estate]: {
                    ...configFormData.availablePlots[estate],
                    status: value
                }
            }
        });
    };

    const handleSaveConfig = async () => {
        // Basic validation
        if (configFormData.plotPrice <= 0 || configFormData.nonMemberPlotPrice <= 0) {
            setConfigError("Plot prices must be greater than zero");
            return;
        }

        if (configFormData.minimumPercentage < 0 || configFormData.minimumPercentage > 100) {
            setConfigError("Minimum percentage must be between 0 and 100");
            return;
        }

        if (configFormData.maximumInstallments <= 0) {
            setConfigError("Maximum installments must be greater than zero");
            return;
        }

        if (configFormData.availablePlots.blossom.available > configFormData.availablePlots.blossom.total ||
            configFormData.availablePlots.university.available > configFormData.availablePlots.university.total) {
            setConfigError("Available plots cannot exceed total plots");
            return;
        }

        setIsSavingConfig(true);
        setConfigError(null);

        try {
            await dispatch(updateSystemConfig(configFormData));
            Swal.fire({
                icon: 'success',
                title: 'Settings Updated',
                text: 'System configuration has been updated successfully'
            });
            setShowConfigModal(false);

            // Refresh data if a callback was provided
            if (refreshCallback && typeof refreshCallback === 'function') {
                refreshCallback();
            }
        } catch (err) {
            setConfigError(err.message || 'Failed to update system configuration');
            Swal.fire({
                icon: 'error',
                title: 'Update Failed',
                text: err.message || 'Failed to update system configuration'
            });
        } finally {
            setIsSavingConfig(false);
        }
    };

    const handleCancel = () => {
        initializeFormData();
        setShowConfigModal(false);
        setConfigError(null);
    };

    return {
        showConfigModal,
        setShowConfigModal,
        configFormData,
        setConfigFormData,
        configError,
        isSavingConfig,
        handleConfigInputChange,
        handleStatusChange,
        handleSaveConfig,
        handleCancel,
        initializeFormData
    };
}